import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Phone, Send, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        inquiry_type: 'conversation'
      });
      setSubmitted(true);
    } catch {
      toast.error('Error sending message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background pt-32 pb-20" data-testid="contact-success">
        <div className="container-custom">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <Check size={40} className="text-primary" />
            </div>
            <h1 className="font-heading text-3xl font-medium text-foreground mb-4">
              Thank you for reaching out.
            </h1>
            <p className="text-muted-foreground text-lg">
              We'll be in touch shortly to arrange a conversation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-32 pb-20" data-testid="contact-page">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Text */}
            <div>
              <p className="text-primary text-sm tracking-[0.2em] uppercase font-medium mb-6">Contact</p>
              <h1 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-6">
                Arrange a Conversation
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                If you'd prefer to speak with us before completing a Private Property Brief, 
                or if you have a general enquiry, we'd be happy to arrange a call.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Contact</p>
                    <p className="text-foreground font-medium">Use the form to get in touch</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white rounded-2xl border p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="text-sm font-medium mb-2 block">Full Name *</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="h-12"
                    data-testid="contact-name"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Email Address *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Telephone / WhatsApp</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+44 7XXX XXX XXX"
                    className="h-12"
                    data-testid="contact-phone"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Message</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help?"
                    rows={4}
                    data-testid="contact-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full" 
                  disabled={submitting}
                  data-testid="contact-submit"
                >
                  {submitting ? 'Sending...' : (
                    <>
                      <Send size={18} className="mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
